/**
 * SidebarContainer Component
 *
 * Unified parent container for the sidebar that can display either:
 * - Chat view (NoteChat)
 * - Audit view (NoteAuditSidebar)
 *
 * Owns the shared header with toggle functionality, eliminating duplicate header logic.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EditIcon } from '@icons/EditIcon';
import {
  ChevronRight as ChevronRightIcon,
  Description as ChatIcon,
  MenuBook as AuditIcon,
} from '@mui/icons-material';
import { Badge, Box, IconButton, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';

import { setChatVisibility } from '../store/chatSlice';

export type SidebarMode = 'chat' | 'audit';

export interface SidebarContainerProps {
  /**
   * Current sidebar mode
   */
  mode: SidebarMode;

  /**
   * Callback when mode changes
   */
  onModeChange: (mode: SidebarMode) => void;

  /**
   * Whether to show the toggle (feature flag controlled)
   */
  showToggle?: boolean;

  /**
   * Number of audit issues to display as badge (computed from RTK Query data)
   */
  auditIssueCount?: number;

  /**
   * Whether there are chat messages (for clear button state)
   */
  hasMessages?: boolean;

  /**
   * Callback when clear chat is requested
   */
  onClearChat?: () => void;

  /**
   * Content to render for chat mode
   */
  chatContent: React.ReactNode;

  /**
   * Content to render for audit mode
   */
  auditContent: React.ReactNode;

  /**
   * Note ID for analytics tracking
   */
  noteId?: string;

  /**
   * Encounter ID for analytics tracking
   */
  encounterId?: string;
}

/**
 * SidebarContainer - Unified container with shared header
 *
 * Provides:
 * - Close button (left) to hide the sidebar
 * - Toggle (center) to switch between chat and audit views
 * - Action button (right) - clear chat when in chat mode
 */
export const SidebarContainer: React.FC<SidebarContainerProps> = ({
  mode,
  onModeChange,
  showToggle = false,
  auditIssueCount = 0,
  hasMessages = false,
  onClearChat,
  chatContent,
  auditContent,
  noteId,
  encounterId,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { noteAudit } = useEventTracking();

  // Track when audit sidebar is opened and how long it's open
  const auditOpenedAtRef = useRef<number | null>(null);
  const [dismissedRulesCount, setDismissedRulesCount] = useState(0);

  // Track when switching to audit mode
  useEffect(() => {
    if (mode === 'audit' && auditOpenedAtRef.current === null) {
      auditOpenedAtRef.current = Date.now();
    } else if (mode !== 'audit' && auditOpenedAtRef.current !== null) {
      auditOpenedAtRef.current = null;
    }
  }, [mode]);

  const handleClose = useCallback(() => {
    // Track sidebar closed if in audit mode
    if (mode === 'audit' && auditOpenedAtRef.current !== null) {
      const duration = Date.now() - auditOpenedAtRef.current;
      noteAudit.sidebarClosed({
        note_id: noteId,
        encounter_id: encounterId,
        duration_ms: duration,
        dismissed_rules_count: dismissedRulesCount,
      });
      auditOpenedAtRef.current = null;
    }
    dispatch(setChatVisibility(false));
  }, [dispatch, mode, noteId, encounterId, dismissedRulesCount, noteAudit]);

  const handleToggleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: SidebarMode | null,
  ) => {
    if (newMode !== null) {
      // Track when user clicks the Audit tab
      if (newMode === 'audit') {
        noteAudit.tabClicked({
          note_id: noteId,
          encounter_id: encounterId,
          previous_mode: mode,
          issue_count: auditIssueCount,
        });
        // Also track sidebarOpened with source
        noteAudit.sidebarOpened({
          note_id: noteId,
          encounter_id: encounterId,
          source: 'tab_toggle',
          issue_count: auditIssueCount,
        });
      }
      onModeChange(newMode);
    }
  };

  const handleClearChat = useCallback(() => {
    onClearChat?.();
  }, [onClearChat]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Shared Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: 48,
          px: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
          bgcolor: theme.palette.background.paper,
          gap: 1,
        }}
      >
        {/* Close Button */}
        <Tooltip title="Hide sidebar" placement="right">
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.text.primary,
                bgcolor: theme.palette.action.hover,
              },
              transition: 'all 0.2s ease',
              flexShrink: 0,
            }}
            aria-label="Hide sidebar"
          >
            <ChevronRightIcon />
          </IconButton>
        </Tooltip>

        {/* Toggle (center) */}
        {showToggle && (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <Badge
              badgeContent={auditIssueCount}
              color="error"
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.65rem',
                  height: 16,
                  minWidth: 16,
                  right: 4,
                  top: 4,
                },
              }}
            >
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={handleToggleChange}
                size="small"
                sx={{
                  bgcolor: theme.palette.grey[100],
                  borderRadius: 2,
                  p: 0.5,
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRadius: 1.5,
                    px: 2,
                    py: 0.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    '&.Mui-selected': {
                      bgcolor: 'background.paper',
                      boxShadow: 1,
                      '&:hover': {
                        bgcolor: 'background.paper',
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="chat">
                  <ChatIcon sx={{ fontSize: 18, mr: 0.75 }} />
                  Edit
                </ToggleButton>
                <ToggleButton value="audit">
                  <AuditIcon sx={{ fontSize: 18, mr: 0.75 }} />
                  Audit
                </ToggleButton>
              </ToggleButtonGroup>
            </Badge>
          </Box>
        )}

        {/* Action Button - Show clear chat only in chat mode */}
        {mode === 'chat' ? (
          <Tooltip title="Start new chat" placement="left">
            <span>
              <IconButton
                onClick={handleClearChat}
                disabled={!hasMessages}
                size="small"
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    color: theme.palette.primary.main,
                    bgcolor: theme.palette.primary.light + '20',
                  },
                  '&:disabled': {
                    color: theme.palette.action.disabled,
                  },
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                }}
                aria-label="Start new chat"
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          // Spacer to maintain layout when no action button
          <Box sx={{ width: 32, flexShrink: 0 }} />
        )}
      </Box>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>{mode === 'chat' ? chatContent : auditContent}</Box>
    </Box>
  );
};
